import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { getDocuments } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Folder,
  File,
  FileText,
  Image,
  FileSpreadsheet,
  Presentation,
  Video,
  Music,
  Archive,
  ExternalLink,
  Loader2,
  Search,
  FolderOpen
} from 'lucide-react';

// Helper to get file icon based on mime type
const getFileIcon = (mimeType) => {
  if (!mimeType) return File;
  if (mimeType.includes('folder')) return Folder;
  if (mimeType.includes('document') || mimeType.includes('text') || mimeType.includes('pdf')) return FileText;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileSpreadsheet;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return Presentation;
  if (mimeType.includes('image')) return Image;
  if (mimeType.includes('video')) return Video;
  if (mimeType.includes('audio')) return Music;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return Archive;
  return File;
};

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const data = await getDocuments();
      setDocuments(data);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="documents-page">
        {/* Header */}
        <div>
          <h1 className="text-heading text-3xl text-slate-800">
            Documentos
          </h1>
          <p className="text-slate-500 mt-1">
            Archivos compartidos de coaching
          </p>
        </div>

        {/* Search */}
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar documentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 glass-input"
                data-testid="search-documents-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Documents List */}
        <Card className="glass-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-blue-900" />
              Todos los Documentos ({filteredDocuments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-blue-900 mx-auto" />
                <p className="text-slate-500 mt-3">Cargando documentos...</p>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Folder className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-heading text-xl text-slate-700 mb-2">
                  {searchTerm ? 'No se encontraron documentos' : 'Sin documentos'}
                </h3>
                <p className="text-slate-500">
                  {searchTerm 
                    ? 'Intenta con otra búsqueda' 
                    : 'No hay documentos disponibles en la carpeta compartida'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredDocuments.map((doc) => {
                  const FileIcon = getFileIcon(doc.mimeType);
                  return (
                    <a
                      key={doc.id}
                      href={doc.webViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-5 flex items-center gap-4 table-row-hover group"
                      data-testid={`document-${doc.id}`}
                    >
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                        doc.isFolder 
                          ? 'bg-amber-100 text-amber-600' 
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        <FileIcon className="w-7 h-7" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 group-hover:text-blue-900 transition-colors truncate text-lg">
                          {doc.name}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          Modificado: {formatDate(doc.modifiedTime)}
                          {doc.size && ` • ${formatFileSize(doc.size)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 group-hover:text-blue-900">
                        <span className="text-sm hidden sm:block">Abrir</span>
                        <ExternalLink className="w-5 h-5" />
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
